import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { AgreementRecord, AgreementTemplate } from "@/types/agreement";
import { AGREEMENT_TYPE_LABELS } from "@/types/agreement";
import { UploadAgreementDialog } from "@/components/agreements/UploadAgreementDialog";

/**
 * Starts an agreement: pick the deal (unless opened from one) and the template to start from.
 * An agreement is made from the deal's accepted quotation, whose lines and terms it carries.
 */
export function NewAgreementDialog({
  open,
  onOpenChange,
  dealId,
  dealName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fixed deal when opened from a deal page */
  dealId?: string;
  dealName?: string;
}) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [deal, setDeal] = useState(dealId || "");
  const [dealLabel, setDealLabel] = useState(dealName || "");
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeal(dealId || "");
    setDealLabel(dealName || "");
    api
      .get("/api/AgreementTemplate/List")
      .then((res) => {
        const list = unwrapArray<AgreementTemplate>(unwrapObject(res));
        setTemplates(list);
        setTemplateId((list.find((t) => t.is_default) || list[0])?.id || "");
      })
      .catch(() => setTemplates([]));
  }, [open, dealId, dealName]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) {
      error("Pick a deal", "Choose the deal this agreement is for");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/api/Agreement/Insert", { deal_id: deal, template_id: templateId || undefined });
      const rec = unwrapObject<AgreementRecord>(res);
      success("Agreement started", `${rec.display_number} is ready to review`);
      onOpenChange(false);
      navigate(`/agreements/${rec.id}`);
    } catch (err: any) {
      error("Couldn't start the agreement", err.message || "Something went wrong");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New agreement</DialogTitle>
          <DialogDescription>
            The clauses are filled with this deal's details, and the price schedule comes from its accepted quotation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={create} className="space-y-4">
          {!dealId && (
            <div className="space-y-1.5">
              <Label className="text-xs">Deal *</Label>
              <SearchSelect
                value={deal}
                selectedLabel={dealLabel}
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                placeholder="Search open deals"
                getLabel={(d) => `${d.name}${d.account?.name ? ` · ${d.account.name}` : ""}${d.stage?.name ? ` (${d.stage.name})` : ""}`}
                onChange={(id, row) => {
                  setDeal(id);
                  setDealLabel(row ? `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}` : "");
                }}
              />
              <p className="text-[11px] text-muted-foreground">The deal needs a quotation the customer has accepted.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Template</Label>
            {templates.length === 0 ? (
              <p className="text-xs text-muted-foreground">No templates; the standard clauses will be used.</p>
            ) : (
              <div className="grid gap-2 max-h-72 overflow-y-auto pr-1">
                {templates.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setTemplateId(t.id)}
                    className={cn("text-left rounded-lg border px-3 py-2 transition-colors", templateId === t.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50")}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{t.name}</span>
                      <span className="flex items-center gap-1.5">
                        {t.is_default && <span className="text-[10px] font-semibold uppercase text-muted-foreground">Default</span>}
                        {templateId === t.id && <Check className="size-4 text-primary" />}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.description || AGREEMENT_TYPE_LABELS[t.agreement_type]}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="ghost" onClick={() => { onOpenChange(false); setUploadOpen(true); }}>
              Use the customer's own PDF instead
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating || !deal}>
                {creating && <Loader2 className="size-4 animate-spin mr-2" />}
                Create agreement
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <UploadAgreementDialog open={uploadOpen} onOpenChange={setUploadOpen} dealId={dealId} dealName={dealName} />
    </>
  );
}
